import { useState, useEffect } from "react";
import { Link } from "@inertiajs/react";
import { ChevronDown } from "lucide-react";
import { ARTICLES } from "@/help/articles";

/**
 * Side-nav rendered next to a role landing or article page.
 *
 * Reads the role's curated section list and renders one bucket per
 * section. Each section header is a disclosure button — click to
 * collapse/expand. The section containing the active article is
 * always expanded; on role landing pages (no active article) every
 * section starts expanded.
 *
 * Wrapping container is responsible for the scroll behaviour
 * (max-height + overflow), so this component just lays out the list.
 *
 * Props:
 *   - role:  one of ROLES[*]
 *   - activeArticleSlug: slug to highlight (optional)
 */
export default function HelpSideNav({ role, activeArticleSlug }) {
  const [openSections, setOpenSections] = useState(() =>
    computeInitialOpen(role, activeArticleSlug)
  );

  // When the active article changes (e.g. user clicked an article in
  // another section), ensure that section is expanded. Other sections
  // keep whatever state the user toggled them into.
  useEffect(() => {
    if (!activeArticleSlug) return;
    const idx = role.sections.findIndex((s) => s.slugs.includes(activeArticleSlug));
    if (idx < 0) return;
    setOpenSections((prev) => (prev[idx] ? prev : { ...prev, [idx]: true }));
  }, [activeArticleSlug, role]);

  const toggle = (idx) => {
    setOpenSections((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <nav className="space-y-3">
      <Link
        href={role.helpPath}
        className="block text-sm font-display font-semibold text-foreground hover:text-primary transition-colors"
      >
        {role.label}
      </Link>

      {role.sections.map((section, idx) => {
        const isOpen = openSections[idx];
        const containsActive = activeArticleSlug && section.slugs.includes(activeArticleSlug);
        return (
          <div key={section.name}>
            <button
              type="button"
              onClick={() => toggle(idx)}
              aria-expanded={isOpen}
              className={`w-full flex items-center justify-between gap-2 px-1 py-1.5 text-[10px] uppercase tracking-wider font-semibold transition-colors ${
                containsActive ? "text-primary" : "text-muted-foreground/80 hover:text-foreground"
              }`}
            >
              <span>{section.name}</span>
              <ChevronDown
                className={`h-3.5 w-3.5 shrink-0 transition-transform ${isOpen ? "" : "-rotate-90"}`}
              />
            </button>
            {isOpen && (
              <ul className="space-y-0.5 mt-1 mb-2">
                {section.slugs.map((slug) => {
                  const a = ARTICLES[slug];
                  if (!a) return null;
                  const active = slug === activeArticleSlug;
                  return (
                    <li key={slug}>
                      <Link
                        href={`/help/articles/${slug}?from=${role.slug}`}
                        className={`block px-2 py-1.5 rounded-md text-sm leading-snug transition-colors ${
                          active
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                        }`}
                      >
                        {a.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function computeInitialOpen(role, activeArticleSlug) {
  // Default: every section starts collapsed. The exception is the
  // section that contains the article you're currently reading — that
  // one auto-expands so the surrounding curriculum is visible without
  // a click. Role landings (no active article) start fully collapsed.
  return role.sections.reduce((acc, s, i) => {
    acc[i] = !!activeArticleSlug && s.slugs.includes(activeArticleSlug);
    return acc;
  }, {});
}
